/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type PropsWithChildren, useContext, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { cssMap, jsx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { token } from '@atlaskit/tokens';

import type { ProductKeys } from '../../constants/product-keys';
import { useAppContext } from '../app-context';
import { PinnedRightPanelContext } from '../layout/right-panel-context';

type BodyProps = PropsWithChildren<{
	bodyRef: React.RefObject<HTMLDivElement>;
	mainContentRef: React.RefObject<HTMLDivElement>;
	isLoading?: boolean;
	rightPanel?: JSX.Element;
	selectedProduct?: ProductKeys;
	setMainContentWidth: React.Dispatch<React.SetStateAction<number | undefined>>;
	sideBySide?: boolean;
	fullWidthSainPortalRef?: React.RefObject<HTMLDivElement>;
	isLiteMode?: boolean;
	fillContainer?: boolean;
	// Passed in rather than derived here: this file can't import the controllers/** hook.
	isRefreshedFilterRowEnabled?: boolean;
}>;

const styles = cssMap({
	fillContainer: {
		flexGrow: 1,
		flexShrink: 0,
	},
	container: {
		overflowY: 'scroll',
		overflowX: 'hidden',
	},
	contentContainer: {
		margin: 'auto',
		maxWidth: '100%',
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		alignItems: 'space-between',
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
	},
	mainContentBase: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'stretch',
		flexGrow: 1,
		// minWidth: '0px' prevents this flex item from expanding past its parent's
		// width when descendants (e.g. SAIN's AnimatedWrapper using width: 100vw)
		// have an intrinsic width larger than the 1120px content container.
		minWidth: '0px',
		zIndex: 0,
	},
	mainContent: {
		// eslint is confused and thinks mainContentLite's styles are part of this block because they share the same prefix
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		gap: token('space.200'),
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		paddingBlock: token('space.250'),
		maxWidth: '768px',
	},
	mainContentRefreshed: {
		paddingTop: token('space.100'),
	},
	mainContentLite: {
		// gap between header and search content is provided by the header component
		gap: 0,
		// eslint is confused and thinks mainContent's styles are part of this block because they share the same prefix
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		rowGap: token('space.300'),
		// eslint-disable-next-line @compiled/shorthand-property-sorting
		paddingTop: '0',
		height: '100%',
		maxWidth: '100%',
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	rightPanelWrapper: {
		display: 'none',
		'@media (min-width: 48rem)': {
			display: 'block',
		},
	},
	rightPanel: {
		position: 'sticky',
		top: 0,
		width: '17rem',
	},
	rightPanelInContainer: {
		position: 'static',
	},
	fullWidthSainPortal: {
		margin: 'auto',
		maxWidth: '100%',
		paddingTop: token('space.250'),
		paddingLeft: token('space.100'),
		paddingRight: token('space.100'),
		'@media (min-width: 48rem)': {
			maxWidth: '70rem',
		},
		'&:empty': {
			display: 'none',
		},
	},
	fullWidthSainPortalRefreshed: {
		paddingTop: '0',
		paddingBottom: token('space.200'),
	},
	fullWidthSainPortalWithPinnedRail: {
		width: '100%',
		paddingLeft: '0',
		paddingRight: '0',
	},
});

export const Body: ({
	selectedProduct,
	rightPanel,
	children,
	bodyRef,
	mainContentRef,
	setMainContentWidth,
	sideBySide,
	fullWidthSainPortalRef,
	isLiteMode,
	isRefreshedFilterRowEnabled,
	fillContainer,
}: BodyProps) => JSX.Element = ({
	selectedProduct,
	rightPanel,
	children,
	bodyRef,
	mainContentRef,
	setMainContentWidth,
	sideBySide,
	fullWidthSainPortalRef,
	isLiteMode,
	isRefreshedFilterRowEnabled,
	fillContainer = false,
}: BodyProps) => {
	const { isNav4Enabled } = useAppContext();
	const rightPanelTarget = useContext(PinnedRightPanelContext);

	useEffect(() => {
		bodyRef?.current?.scrollTo?.(0, 0);
	}, [selectedProduct, bodyRef, isNav4Enabled]);

	useEffect(() => {
		const callback = () => {
			if (mainContentRef.current) {
				setMainContentWidth(mainContentRef.current.offsetWidth);
			}
		};

		const resizeObserver = new ResizeObserver(callback);

		if (mainContentRef.current) {
			resizeObserver.observe(mainContentRef.current);
			// Set initial width
			callback();
		}

		return () => {
			resizeObserver.disconnect();
		};
		// These dependencies should never change
	}, [mainContentRef, setMainContentWidth]);

	const hasPinnedRail = rightPanelTarget !== undefined && !sideBySide && Boolean(rightPanel);
	const answerPortal = fullWidthSainPortalRef && (
		<div
			css={[
				styles.fullWidthSainPortal,
				isRefreshedFilterRowEnabled && styles.fullWidthSainPortalRefreshed,
				hasPinnedRail && styles.fullWidthSainPortalWithPinnedRail,
			]}
			ref={fullWidthSainPortalRef}
			data-testid="search-page-full-width-sain-portal"
		/>
	);

	return (
		<div
			css={[!fillContainer && styles.container, fillContainer && styles.fillContainer]}
			ref={fillContainer ? undefined : bodyRef}
			data-testid="search-page-body"
		>
			{hasPinnedRail ? (
				<Inline space="space.800" xcss={styles.contentContainer}>
					<Box xcss={styles.mainContentBase}>{answerPortal}</Box>
					<Box xcss={styles.rightPanelWrapper}>
						<Box xcss={styles.rightPanel} />
					</Box>
				</Inline>
			) : (
				answerPortal
			)}
			<Inline space="space.800" xcss={styles.contentContainer}>
				<div
					css={[
						styles.mainContentBase,
						isLiteMode ? styles.mainContentLite : styles.mainContent,
						isRefreshedFilterRowEnabled && styles.mainContentRefreshed,
					]}
					ref={mainContentRef}
					data-testid="search-page-body-main-content"
				>
					{children}
				</div>
				{!sideBySide && rightPanel && (
					<Box xcss={styles.rightPanelWrapper}>
						<div css={[styles.rightPanel, fillContainer && styles.rightPanelInContainer]}>
							{rightPanelTarget === undefined
								? rightPanel
								: rightPanelTarget && createPortal(rightPanel, rightPanelTarget)}
						</div>
					</Box>
				)}
			</Inline>
		</div>
	);
};
